import React, { useState, useCallback, useEffect, useRef } from 'react';
import Header from './components/Header';
import { Sidebar } from './components/Sidebar';
import { WorkflowCanvas } from './components/WorkflowCanvas';
import { SettingsPanel } from './components/SettingsPanel';
import { CredentialsModal } from './components/CredentialsModal';
import { LogPanel } from './components/LogPanel';
import { generateWorkflowFromPrompt, generateNodeFromPrompt, streamWorkflowFromPrompt } from './services/geminiService.js';
import { executeWorkflow } from './services/executionService.js';
import { NODE_TYPES, NODE_WIDTH, NODE_HEIGHT } from './constants.js';
import DelegationPanel from './components/DelegationPanel';
import NotificationPanel from './components/NotificationPanel';
import ErrorModal from './components/ErrorModal';
import AlertModal from './components/AlertModal';
import QueryHistory from './components/QueryHistory';
import { BusinessPlanNode } from './components/BusinessPlanNode';
import workspaceService from './services/workspaceService.js';
import { WorkspacesPanel } from './components/WorkspacesPanel.js';
import { TodoPanel } from './components/TodoPanel.js';
import { sendBusinessPlanToTelegram } from './services/businessPlanTelegramService.js';
import WorkflowSignupModal from '../shared/WorkflowSignupModal.js';

const WORKFLOW_STORAGE_KEY = 'busin.us-workflow-state';
const CREDENTIALS_STORAGE_KEY = 'busin.us-credentials';

// Custom hook for managing state history, moved outside the component
const useHistory = (initialStateFunc) => {
  const [history, setHistory] = useState(() => [initialStateFunc()]);
  const [index, setIndex] = useState(0);

  const setState = useCallback((
    action,
    overwrite = false
  ) => {
    const newState = typeof action === 'function' ? action(history[index]) : action;
    if (overwrite) {
      const newHistory = [...history];
      newHistory[index] = newState;
      setHistory(newHistory);
    } else {
      const newHistory = history.slice(0, index + 1);
      newHistory.push(newState);
      setHistory(newHistory);
      setIndex(newHistory.length - 1);
    }
  }, [history, index]);

  const undo = useCallback(() => {
    if (index > 0) setIndex(i => i - 1);
  }, [index]);

  const redo = useCallback(() => {
    // FIX: Corrected redo logic to increment index instead of decrementing
    if (index < history.length - 1) setIndex(i => i + 1);
  }, [index, history.length]);

  return {
    state: history[index],
    setState,
    undo,
    redo,
    canUndo: index > 0,
    canRedo: index < history.length - 1,
  };
};

const getInitialStateFromStorage = () => {
  try {
    const savedState = localStorage.getItem(WORKFLOW_STORAGE_KEY);
    if (savedState) {
      const parsedState = JSON.parse(savedState);
      if (Array.isArray(parsedState.nodes) && Array.isArray(parsedState.edges)) {
        return {
            nodes: parsedState.nodes,
            edges: parsedState.edges,
            groups: parsedState.groups || [],
        };
      }
    }
  } catch (e) {
    console.error("Failed to load workflow from localStorage", e);
  }
  return { nodes: [], edges: [], groups: [] };
};

const getInitialCredentials = () => {
    try {
        const savedCreds = localStorage.getItem(CREDENTIALS_STORAGE_KEY);
        if (savedCreds) {
            const parsed = JSON.parse(savedCreds);
            if(Array.isArray(parsed)) return parsed;
        }
    } catch (e) {
        console.error("Failed to load credentials from localStorage", e);
    }
    return [];
};

const WorkflowApp = () => {
  const { state, setState, undo, redo, canUndo, canRedo } = useHistory(getInitialStateFromStorage);
  
  // Add workspace state - initialize as empty array to prevent .find errors
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState(null);
  const [showWorkspacesPanel, setShowWorkspacesPanel] = useState(false);
  const [showTodoPanel, setShowTodoPanel] = useState(false); // Add state for todo panel
  
  // Business plan text state
  const [businessPlanText, setBusinessPlanText] = useState(() => {
    // Initialize from localStorage
    try {
      const savedBusinessPlan = localStorage.getItem('busin.us-business-plan');
      return savedBusinessPlan || '';
    } catch (e) {
      console.error("Failed to load business plan from localStorage", e);
      return '';
    }
  });
  
  // Guard against undefined state during initialization race conditions
  if (!state) {
    return null;
  }

  const { nodes, edges, groups } = state;
  const [selectedNodeIds, setSelectedNodeIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false); // New state for streaming
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [credentials, setCredentials] = useState(getInitialCredentials);
  const [isCredentialsModalOpen, setIsCredentialsModalOpen] = useState(false);
  const [executionStatus, setExecutionStatus] = useState('IDLE');
  const [executionLogs, setExecutionLogs] = useState([]); // Store execution logs
  const [nodeComments, setNodeComments] = useState({}); // Store comments for each node
  const [activeNodeId, setActiveNodeId] = useState(null);
  const [aiPrompt, setAiPrompt] = useState(''); // Add this state for the AI prompt
  
  // Mobile state
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isDelegationOpen, setIsDelegationOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [delegatedNodeId, setDelegatedNodeId] = useState(null);
  const [isQueryHistoryOpen, setIsQueryHistoryOpen] = useState(false);
  
  // Signup modal state
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [pendingExport, setPendingExport] = useState(false);
  
  // Alert modal state
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });
  
  const dragStartPositionsRef = useRef(new Map());
  const clipboardRef = useRef({ nodes: [], edges: [] });
  const isDraggingRef = useRef(false);

  // Check if we're on mobile device
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 640); // Tailwind's sm breakpoint
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // Load workspace from storage
  useEffect(() => {
    const loadWorkspaces = async () => {
      try {
        const workspaces = await workspaceService.getAllWorkspaces();
        setWorkspaces(workspaces || []); // Ensure it's always an array
        
        // If there are no workspaces, create a default one
        if (!workspaces || workspaces.length === 0) {
          try {
            const defaultWorkspace = await workspaceService.createWorkspace('Default Workspace');
            const updatedWorkspaces = await workspaceService.getAllWorkspaces();
            setWorkspaces(updatedWorkspaces || [defaultWorkspace]);
            setCurrentWorkspaceId(defaultWorkspace.id);
          } catch (e) {
            console.warn('Could not create default workspace, using local storage only', e);
            setWorkspaces([]);
            setCurrentWorkspaceId(null);
          }
        } else {
          // Load the first workspace or the last active one
          const lastActiveWorkspaceId = localStorage.getItem('busin.us-last-active-workspace');
          const workspaceToLoad = workspaces.find(w => w.id === lastActiveWorkspaceId) || workspaces[0];
          setCurrentWorkspaceId(workspaceToLoad.id);
          
          // Load the workspace data into the state
          setState({
            nodes: workspaceToLoad.nodes || workspaceToLoad.canvas_data?.nodes || [],
            edges: workspaceToLoad.edges || workspaceToLoad.canvas_data?.edges || [],
            groups: workspaceToLoad.groups || workspaceToLoad.canvas_data?.groups || []
          });
        }
      } catch (error) {
        console.error('Failed to load workspaces:', error);
        // Fallback: create a default workspace
        try {
          const defaultWorkspace = await workspaceService.createWorkspace('Default Workspace');
          setWorkspaces([defaultWorkspace]);
          setCurrentWorkspaceId(defaultWorkspace.id);
        } catch (createError) {
          console.error('Failed to create default workspace:', createError);
          setWorkspaces([]);
        }
      }
    };
    
    loadWorkspaces();
  }, [setState]);

  // Save current workspace when state changes
  useEffect(() => {
    if (currentWorkspaceId) {
      workspaceService.updateWorkspace(currentWorkspaceId, {
        nodes: state.nodes,
        edges: state.edges,
        groups: state.groups
      });
      setWorkspaces(workspaceService.getAllWorkspaces());
    }
  }, [state, currentWorkspaceId]);

  // Save last active workspace
  useEffect(() => {
    if (currentWorkspaceId) {
      localStorage.setItem('busin.us-last-active-workspace', currentWorkspaceId);
    }
  }, [currentWorkspaceId]);

  useEffect(() => {
    try {
      localStorage.setItem(WORKFLOW_STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error("Failed to save workflow to localStorage", e);
    }
  }, [state]);

  useEffect(() => {
    try {
        localStorage.setItem(CREDENTIALS_STORAGE_KEY, JSON.stringify(credentials));
    } catch(e) {
        console.error("Failed to save credentials to localStorage", e);
    }
  }, [credentials]);

  const addCredential = useCallback((name, type, data) => {
    const newCredential = {
        id: `cred_${Date.now()}`,
        name,
        type,
        data,
    };
    setCredentials(prev => [...prev, newCredential]);
  }, []);

  const deleteCredential = useCallback((id) => {
    setCredentials(prev => prev.filter(c => c.id !== id));
    // Also remove this credentialId from any nodes that are using it
    setState(prevState => ({
        ...prevState,
        nodes: prevState.nodes.map(node => {
            if (node.data.credentialId === id) {
                const newData = { ...node.data, credentialId: '' };
                return { ...node, data: newData };
            }
            return node;
        })
    }), true); // overwrite history
  }, [setState]);

  // Function to add a comment to a node
  const addNodeComment = useCallback((nodeId, commentText) => {
    const newComment = {
      id: Date.now(),
      text: commentText,
      timestamp: new Date().toISOString()
    };
    
    setNodeComments(prevComments => ({
      ...prevComments,
      [nodeId]: [...(prevComments[nodeId] || []), newComment]
    }));
  }, []);

  const addNode = useCallback((type, label) => {
    const newNodeId = `node_${Date.now()}`;
    const nodeDefaults = NODE_TYPES[type] || NODE_TYPES.NOVEL_NODE;
    
    const newNode = {
      id: newNodeId,
      type: type,
      label: label || nodeDefaults.label,
      position: { x: 100 + (nodes.length % 5) * 250, y: 100 + Math.floor(nodes.length / 5) * 150 },
      data: { ...nodeDefaults.defaultData },
    };

    setState(prevState => ({ ...prevState, nodes: [...prevState.nodes, newNode] }));
    setSelectedNodeIds([newNodeId]);
  }, [nodes.length, setState]);

  const handleAiAddNode = useCallback(async (prompt) => {
    try {
        console.log("Generating node with prompt:", prompt);
        const { type, label } = await generateNodeFromPrompt(prompt);
        console.log("Generated node:", { type, label });
        addNode(type, label);
        setAiPrompt(''); // Clear the prompt after successful generation
    } catch (err) {
        console.error("Error in handleAiAddNode:", err);
        setError('Failed to generate node. Please try a different prompt.');
        setErrorDetails({
          message: err.message,
          stack: err.stack,
          prompt: prompt
        });
        setTimeout(() => setError(null), 5000);
    }
  }, [addNode]);
  
  const handleNodeClick = useCallback((nodeId, isShiftPressed) => {
    setSelectedNodeIds(prevSelected => {
      if (isShiftPressed) {
        const isSelected = prevSelected.includes(nodeId);
        return isSelected ? prevSelected.filter(id => id !== nodeId) : [...prevSelected, nodeId];
      }
      if (prevSelected.includes(nodeId) && prevSelected.length === 1) {
        return prevSelected;
      }
      return [nodeId];
    });
    
    // On mobile, open settings panel when a node is selected
    if (isMobile) {
      setIsSettingsOpen(true);
    }
  }, [isMobile]);

  const handleSelectNodes = useCallback((nodeIds, isShiftPressed) => {
    setSelectedNodeIds(prevSelected => {
      if (isShiftPressed) {
        const newIds = new Set([...prevSelected, ...nodeIds]);
        return Array.from(newIds);
      }
      return nodeIds;
    });
  }, []);

  const addEdge = useCallback((edge) => {
    setState(prevState => {
      const filteredEdges = prevState.edges.filter(e => !(e.source === edge.source && e.sourceHandle === edge.sourceHandle));
      const newEdge = {
        id: `edge_${edge.source}_${edge.sourceHandle || 'default'}-${edge.target}`,
        ...edge
      };
      return { ...prevState, edges: [...filteredEdges, newEdge] };
    });
  }, [setState]);

  const updateNode = useCallback((nodeId, updates) => {
    setState(prevState => ({
      ...prevState,
      nodes: prevState.nodes.map(node => {
        if (node.id === nodeId) {
          const updatedNode = { ...node };
          if (updates.label !== undefined) updatedNode.label = updates.label;
          if (updates.data) updatedNode.data = { ...node.data, ...updates.data };
          return updatedNode;
        }
        return node;
      })
    }));
  }, [setState]);

  const updateNodePosition = useCallback((draggedNodeId, newPosition) => {
    setState(prevState => {
        const startPositions = dragStartPositionsRef.current;
        const draggedNodeStartPos = startPositions.get(draggedNodeId);
        if (!draggedNodeStartPos) return prevState;

        const dx = newPosition.x - draggedNodeStartPos.x;
        const dy = newPosition.y - draggedNodeStartPos.y;

        const nodesToDrag = selectedNodeIds.length > 0 && selectedNodeIds.includes(draggedNodeId)
            ? selectedNodeIds
            : [draggedNodeId];

        let tempNodes = prevState.nodes.map(n => ({ ...n, position: { ...n.position } }));

        // Step 1: Move the dragged nodes
        tempNodes.forEach(node => {
            if (nodesToDrag.includes(node.id)) {
                const startPos = startPositions.get(node.id);
                if (startPos) {
                    node.position.x = startPos.x + dx;
                    node.position.y = startPos.y + dy;
                }
            }
        });

        // Step 2: Apply repulsion physics
        const ITERATIONS = 5;
        const PADDING = 5;

        for (let i = 0; i < ITERATIONS; i++) {
            for (let j = 0; j < tempNodes.length; j++) {
                for (let k = j + 1; k < tempNodes.length; k++) {
                    const nodeA = tempNodes[j];
                    const nodeB = tempNodes[k];

                    const isNodeADragged = nodesToDrag.includes(nodeA.id);
                    const isNodeBDragged = nodesToDrag.includes(nodeB.id);

                    if (isNodeADragged && isNodeBDragged) continue;

                    const rectA = { x: nodeA.position.x, y: nodeA.position.y, width: NODE_WIDTH, height: NODE_HEIGHT };
                    const rectB = { x: nodeB.position.x, y: nodeB.position.y, width: NODE_WIDTH, height: NODE_HEIGHT };
                    
                    const centerA = { x: rectA.x + rectA.width / 2, y: rectA.y + rectA.height / 2 };
                    const centerB = { x: rectB.x + rectB.width / 2, y: rectB.y + rectB.height / 2 };
                    
                    const deltaX = centerA.x - centerB.x;
                    const deltaY = centerA.y - centerB.y;

                    const overlapX = (rectA.width + rectB.width) / 2 - Math.abs(deltaX);
                    const overlapY = (rectA.height + rectB.height) / 2 - Math.abs(deltaY);

                    if (overlapX > 0 && overlapY > 0) {
                        const moveX = overlapX + PADDING;
                        const moveY = overlapY + PADDING;

                        if (overlapX < overlapY) {
                            const direction = deltaX > 0 ? 1 : -1;
                            const forceA = !isNodeADragged ? (isNodeBDragged ? moveX : moveX / 2) : 0;
                            const forceB = !isNodeBDragged ? (isNodeADragged ? moveX : moveX / 2) : 0;
                            nodeA.position.x += forceA * direction;
                            nodeB.position.x -= forceB * direction;
                        } else {
                            const direction = deltaY > 0 ? 1 : -1;
                            const forceA = !isNodeADragged ? (isNodeBDragged ? moveY : moveY / 2) : 0;
                            const forceB = !isNodeBDragged ? (isNodeADragged ? moveY : moveY / 2) : 0;
                            nodeA.position.y += forceA * direction;
                            nodeB.position.y -= forceB * direction;
                        }
                    }
                }
            }
        }
        return { ...prevState, nodes: tempNodes };
    }, true);
  }, [setState, selectedNodeIds]);

  const onNodeDragStart = useCallback((draggedNodeId) => {
    isDraggingRef.current = true;
    const posMap = new Map();
    const nodeMap = new Map(nodes.map(n => [n.id, n]));

    const idsToTrack = selectedNodeIds.length > 0 && selectedNodeIds.includes(draggedNodeId)
      ? selectedNodeIds
      : [draggedNodeId];
    
    idsToTrack.forEach(id => {
      const node = nodeMap.get(id);
      if (node) {
        posMap.set(id, node.position);
      }
    });

    dragStartPositionsRef.current = posMap;
  }, [nodes, selectedNodeIds]);

  const onNodeDragStop = useCallback(() => {
    if(!isDraggingRef.current) return;
    isDraggingRef.current = false;
    dragStartPositionsRef.current.clear();
    setState(state);
  }, [state, setState]);

  const deleteNodes = useCallback((nodeIds) => {
    setState(prevState => {
        const newNodes = prevState.nodes.filter(n => !nodeIds.includes(n.id));
        const newEdges = prevState.edges.filter(e => !nodeIds.includes(e.source) && !nodeIds.includes(e.target));
        const newGroups = prevState.groups.map(g => ({
            ...g,
            nodeIds: g.nodeIds.filter(id => !nodeIds.includes(id))
        })).filter(g => g.nodeIds.length > 0);

        return { nodes: newNodes, edges: newEdges, groups: newGroups };
    });
    setSelectedNodeIds([]);
  }, [setState]);

  const groupSelectedNodes = useCallback(() => {
    if (selectedNodeIds.length < 2) return;

    setState(prevState => {
        const selectedNodes = prevState.nodes.filter(n => selectedNodeIds.includes(n.id));
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        
        selectedNodes.forEach(n => {
            minX = Math.min(minX, n.position.x);
            minY = Math.min(minY, n.position.y);
            maxX = Math.max(maxX, n.position.x + NODE_WIDTH);
            maxY = Math.max(maxY, n.position.y + NODE_HEIGHT);
        });

        const PADDING = 40;
        const newGroup = {
            id: `group_${Date.now()}`,
            label: 'New Group',
            position: { x: minX - PADDING, y: minY - PADDING },
            size: { width: maxX - minX + PADDING * 2, height: maxY - minY + PADDING * 2 },
            nodeIds: [...selectedNodeIds],
            isCollapsed: false,
            color: ['#4f46e5', '#db2777', '#16a34a', '#d97706'][prevState.groups.length % 4],
        };

        return { ...prevState, groups: [...prevState.groups, newGroup] };
    });
    setSelectedNodeIds([]);
  }, [selectedNodeIds, setState]);

  const onGroupDragStart = useCallback((groupId) => {
    isDraggingRef.current = true;
    const group = groups.find(g => g.id === groupId);
    if (!group) return;

    const posMap = new Map();
    posMap.set(groupId, group.position);

    const nodeMap = new Map(nodes.map(n => [n.id, n]));
    group.nodeIds.forEach(nodeId => {
        const node = nodeMap.get(nodeId);
        if (node) posMap.set(nodeId, node.position);
    });
    
    dragStartPositionsRef.current = posMap;
  }, [nodes, groups]);
  
  const updateGroupPosition = useCallback((groupId, newPosition) => {
    setState(prevState => {
        const startPositions = dragStartPositionsRef.current;
        const groupStartPos = startPositions.get(groupId);
        if (!groupStartPos) return prevState;

        const dx = newPosition.x - groupStartPos.x;
        const dy = newPosition.y - groupStartPos.y;

        const tempGroups = prevState.groups.map(g => {
            if (g.id === groupId) {
                const startPos = startPositions.get(g.id);
                return { ...g, position: { x: startPos.x + dx, y: startPos.y + dy } };
            }
            return g;
        });

        const group = prevState.groups.find(g => g.id === groupId);
        const tempNodes = prevState.nodes.map(n => {
            if (group.nodeIds.includes(n.id)) {
                const startPos = startPositions.get(n.id);
                return { ...n, position: { x: startPos.x + dx, y: startPos.y + dy } };
            }
            return n;
        });

        return { ...prevState, nodes: tempNodes, groups: tempGroups };
    }, true);
  }, [setState]);

  const toggleGroupCollapse = useCallback((groupId) => {
    setState(prevState => ({
        ...prevState,
        groups: prevState.groups.map(g => g.id === groupId ? { ...g, isCollapsed: !g.isCollapsed } : g)
    }));
  }, [setState]);

  const handleOpenDelegationPanel = useCallback((nodeId) => {
    setDelegatedNodeId(nodeId);
    // Only open the delegation panel on mobile devices when a node is selected
    if (isMobile) {
      setIsDelegationOpen(true);
    }
  }, [isMobile]);

  const handleOpenNotifications = useCallback(() => {
    setIsNotificationOpen(prev => !prev);
    if (isMobile) {
      // On mobile, we might want to handle this differently
    }
  }, [isMobile]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const isInputFocused = event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement;
      if (isInputFocused) return;

      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedNodeIds.length > 0) {
        deleteNodes(selectedNodeIds);
      }
      
      const isUndo = (event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey;
      const isRedo = (event.ctrlKey || event.metaKey) && (event.key === 'y' || (event.shiftKey && event.key === 'z'));
      const isCopy = (event.ctrlKey || event.metaKey) && event.key === 'c';
      const isPaste = (event.ctrlKey || event.metaKey) && event.key === 'v';
      const isGroup = (event.ctrlKey || event.metaKey) && event.key === 'g';
      
      if (isGroup) { event.preventDefault(); groupSelectedNodes(); }
      if (isUndo) { event.preventDefault(); undo(); }
      if (isRedo) { event.preventDefault(); redo(); }

      if (isCopy && selectedNodeIds.length > 0) {
        event.preventDefault();
        const copiedNodes = nodes.filter(n => selectedNodeIds.includes(n.id));
        const copiedEdges = edges.filter(e => selectedNodeIds.includes(e.source) && selectedNodeIds.includes(e.target));
        clipboardRef.current = { nodes: copiedNodes, edges: copiedEdges };
      }

      if (isPaste && clipboardRef.current.nodes.length > 0) {
        event.preventDefault();
        const { nodes: copiedNodes, edges: copiedEdges } = clipboardRef.current;
        const idMapping = new Map();
        const newNodes = [];
        const newEdges = [];
        
        const findOpenPosition = (x, y, existingNodes) => {
            let newX = x;
            let newY = y;
            let collision = true;
            while(collision) {
                collision = false;
                for (const node of existingNodes) {
                    const rectA = { x: newX, y: newY, width: NODE_WIDTH, height: NODE_HEIGHT };
                    const rectB = { x: node.position.x, y: node.position.y, width: NODE_WIDTH, height: NODE_HEIGHT };
                    const overlapX = Math.max(0, Math.min(rectA.x + rectA.width, rectB.x + rectB.width) - Math.max(rectA.x, rectB.x));
                    const overlapY = Math.max(0, Math.min(rectA.y + rectA.height, rectB.y + rectB.height) - Math.max(rectA.y, rectB.y));
                    if (overlapX * overlapY > 0) {
                        collision = true;
                        newX += NODE_WIDTH / 4;
                        newY += NODE_HEIGHT / 4;
                        break;
                    }
                }
            }
            return { x: newX, y: newY };
        }

        copiedNodes.forEach(node => {
          const newId = `node_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
          idMapping.set(node.id, newId);
          const pos = findOpenPosition(node.position.x + 20, node.position.y + 20, [...nodes, ...newNodes]);
          newNodes.push({
            ...node,
            id: newId,
            position: pos,
            warnings: undefined,
          });
        });

        copiedEdges.forEach(edge => {
          const newSourceId = idMapping.get(edge.source);
          const newTargetId = idMapping.get(edge.target);
          if (newSourceId && newTargetId) {
            newEdges.push({
              ...edge,
              id: `edge_${newSourceId}_${edge.sourceHandle || 'default'}-${newTargetId}`,
              source: newSourceId,
              target: newTargetId,
            });
          }
        });

        setState(prevState => ({
          ...prevState,
          nodes: [...prevState.nodes, ...newNodes],
          edges: [...prevState.edges, ...newEdges],
        }));
        setSelectedNodeIds(newNodes.map(n => n.id));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedNodeIds, deleteNodes, undo, redo, setState, nodes, edges, groupSelectedNodes]);

  useEffect(() => {
    const warnings = new Map();
    nodes.forEach(node => {
      const nodeWarnings = [];
      const isConnectedIn = edges.some(e => e.target === node.id);
      const isConnectedOut = edges.some(e => e.source === node.id);
      if (node.type !== 'TRIGGER' && !isConnectedIn) nodeWarnings.push('Node has no input connection.');
      const terminalTypes = ['LOOP_END', 'SEND_EMAIL', 'LOGGER'];
      if (!terminalTypes.includes(node.type) && !isConnectedOut) nodeWarnings.push('Node has no output connection.');
      
      // Field validations
      if (node.type === 'SEND_EMAIL' && !node.data.to?.trim()) nodeWarnings.push("'To' field in email is missing.");
      if (node.type === 'CONNECT_API' && !node.data.url?.trim()) nodeWarnings.push("'URL' field in API connection is missing.");
      if (node.type === 'BROWSING_AGENT' && !node.data.url?.trim()) nodeWarnings.push("'URL' field for browsing agent is missing.");
      if (node.type === 'REGISTER_BUSINESS' && !node.data.businessName?.trim()) nodeWarnings.push("'Business Name' field is missing.");
      if (node.type === 'CREATE_WEBSITE' && !node.data.siteName?.trim()) nodeWarnings.push("'Site Name' field is missing.");
      if (node.type === 'MANAGE_INVOICES' && (!node.data.client?.trim() || !node.data.amount)) nodeWarnings.push("'Client' and 'Amount' fields are missing.");
      if (node.type === 'MARKET_RESEARCH' && !node.data.topic?.trim()) nodeWarnings.push("'Topic' field is missing.");
      if (node.type === 'COMPLIANCE_CHECK' && !node.data.checkType?.trim()) nodeWarnings.push("'Check Type' field is missing.");
      if (node.type === 'HIRE_EMPLOYEE' && !node.data.role?.trim()) nodeWarnings.push("'Role' field is missing.");

      // Credential validation
      if ((node.type === 'CONNECT_API' || node.type === 'QUERY_DATABASE') && !node.data.credentialId) {
        nodeWarnings.push("Credential not selected.");
      }

      if (nodeWarnings.length > 0) warnings.set(node.id, nodeWarnings);
    });

    let warningsChanged = nodes.some(n => JSON.stringify(n.warnings || []) !== JSON.stringify(warnings.get(n.id) || []));
    if (warningsChanged) {
      setState(prevState => ({
        ...prevState,
        nodes: prevState.nodes.map(n => {
          const nodeWarnings = warnings.get(n.id) || [];
          return nodeWarnings.length > 0 ? { ...n, warnings: nodeWarnings } : { ...n, warnings: undefined };
        })
      }), true);
    }
  }, [nodes, edges, setState]);

  const handleExport = useCallback(async () => {
    // Check if user has already signed up
    const signupData = localStorage.getItem('busin.us-signup');
    
    if (signupData) {
      // User has already signed up, proceed with export
      try {
        // Export to local file
        const workflowData = JSON.stringify({ nodes, edges, groups }, null, 2);
        const blob = new Blob([workflowData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `workflow-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        // Send to Telegram through the secure backend API
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/send-telegram-notification`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-API-Key': process.env.REACT_APP_SERVER_API_KEY,
            },
            body: JSON.stringify({
              name: userName,
              email: userEmail,
              workflowData: { nodes, edges, groups },
              notificationType: 'workflow_export'
            })
          });
          
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
          }
          
          const result = await response.json();
          console.log("Telegram notification result:", result);
        } catch (error) {
          console.error("Error sending notification through backend:", error);
        }
        
        return true;
      } catch (error) {
        console.error("Error sending Telegram message:", error);
        return false;
      }
    } else {
      // User hasn't signed up yet, show signup modal
      setPendingExport(true);
      setIsSignupModalOpen(true);
      return false;
    }
  }, [nodes, edges, groups]);

  // Handle export after signup
  const handleSignupExport = useCallback(async () => {
    try {
      // Export to local file
      const workflowData = JSON.stringify({ nodes, edges, groups }, null, 2);
      const blob = new Blob([workflowData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `workflow-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      // Send to Telegram through the secure backend API
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/send-telegram-notification`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': process.env.REACT_APP_SERVER_API_KEY,
          },
          body: JSON.stringify({
            name: userName,
            email: userEmail,
            workflowData: { nodes, edges, groups },
            notificationType: 'workflow_export'
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        console.log("Telegram notification result:", result);
      } catch (error) {
        console.error("Error sending notification through backend:", error);
      }
      
      return true;
    } catch (error) {
      console.error("Error sending Telegram message:", error);
      return false;
    }
  }, [nodes, edges, groups]);

  const handleImport = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';

    input.accept = 'application/json';
    
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) {
        setError('No file selected.');
        setTimeout(() => setError(null), 5000);
        return;
      }

      try {
        const content = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (event) => resolve(event.target.result);
          reader.onerror = () => reject(new Error('Failed to read file.'));
          reader.readAsText(file);
        });
        
        const data = JSON.parse(content);
        
        // Validate the structure
        if (!data || typeof data !== 'object') {
          throw new Error('Invalid file format. Expected a JSON object.');
        }
        
        // Check if it's a workflow file or a general JSON
        if (Array.isArray(data.nodes) && Array.isArray(data.edges)) {
          // It's a workflow file
          setState({ 
            nodes: Array.isArray(data.nodes) ? data.nodes : [], 
            edges: Array.isArray(data.edges) ? data.edges : [], 
            groups: Array.isArray(data.groups) ? data.groups : [] 
          });
        } else {
          // Try to parse as a general workflow object
          const workflowData = data.workflow || data;
          if (Array.isArray(workflowData.nodes) && Array.isArray(workflowData.edges)) {
            setState({ 
              nodes: workflowData.nodes, 
              edges: workflowData.edges, 
              groups: Array.isArray(workflowData.groups) ? workflowData.groups : [] 
            });
          } else {
            throw new Error('Invalid workflow file structure. Missing nodes or edges.');
          }
        }
        
        setSelectedNodeIds([]);
        setError(null);
      } catch (err) {
        console.error("Failed to import workflow:", err);
        setError(`Failed to import workflow: ${err.message}`);
        setTimeout(() => setError(null), 5000);
      }
    };
    
    input.click();
  }, [setState]);

  // New function to import workflow to a new workspace
  const handleImportToNewWorkspace = useCallback((workflowData) => {
    try {
      // Validate the structure
      if (!workflowData || typeof workflowData !== 'object') {
        throw new Error('Invalid workflow data structure.');
      }
      
      if (Array.isArray(workflowData.nodes) && Array.isArray(workflowData.edges)) {
        // It's a valid workflow structure
        setState({ 
          nodes: Array.isArray(workflowData.nodes) ? workflowData.nodes : [], 
          edges: Array.isArray(workflowData.edges) ? workflowData.edges : [], 
          groups: Array.isArray(workflowData.groups) ? workflowData.groups : [] 
        });
        setSelectedNodeIds([]);
        setError(null);
      } else {
        throw new Error('Invalid workflow file structure. Missing nodes or edges.');
      }
    } catch (err) {
      console.error("Failed to import workflow to new workspace:", err);
      setError(`Failed to import workflow: ${err.message}`);
      setTimeout(() => setError(null), 5000);
    }
  }, [setState]);

  const handleAiGenerate = async (prompt) => {
    // No waitlist check needed
    setIsLoading(true);
    setIsStreaming(true); // Set streaming to true
    setError(null);
    setErrorDetails(null);
    setSelectedNodeIds([]);
    
    // Save query to history
    try {
      const QUERY_HISTORY_STORAGE_KEY = 'busin.us-query-history';
      const savedHistory = localStorage.getItem(QUERY_HISTORY_STORAGE_KEY);
      let history = [];
      
      if (savedHistory) {
        history = JSON.parse(savedHistory);
      }
      
      // Add new query to history
      const trimmedQuery = prompt.trim();
      if (trimmedQuery) {
        // Remove duplicates
        const filteredHistory = history.filter(item => item.query !== trimmedQuery);
        const newHistory = [
          {
            id: Date.now(),
            query: trimmedQuery,
            timestamp: new Date().toISOString()
          },
          ...filteredHistory
        ];
        
        // Keep only the last 20 queries
        const finalHistory = newHistory.slice(0, 20);
        localStorage.setItem(QUERY_HISTORY_STORAGE_KEY, JSON.stringify(finalHistory));
      }
    } catch (e) {
      console.warn('Failed to save query to history:', e);
    }
    
    // Clear existing nodes to start fresh
    setState({ nodes: [], edges: [], groups: [] });
    
    try {
      console.log("Generating workflow with prompt:", prompt);
      
      // Use refs to keep track of current nodes and edges during streaming
      const currentNodesRef = { current: [] };
      const currentEdgesRef = { current: [] };
      let hasTodoList = false;
      let nodeCounter = 0;
      
      // Tree layout variables
      const LAYER_HORIZONTAL_SPACING = 250;
      const LAYER_VERTICAL_SPACING = 120;
      const layers = {}; // Track nodes by layer for better positioning
      
      // Use the streaming function to receive nodes as they're generated
      for await (const genNode of streamWorkflowFromPrompt(prompt, (node) => {
        console.log("Received node from stream:", node);
        
        // This callback will be called for each node as it's received
        const nodeId = `node_ai_${Date.now()}_${nodeCounter++}`;
        const nodeType = node.type;
        const nodeDefaults = NODE_TYPES[nodeType] || NODE_TYPES.NOVEL_NODE;
      
        if (nodeType === 'TODO_VIEW') {
          // Position TODO_VIEW node separately at the bottom right
          const todoViewNode = { 
            id: nodeId, 
            type: nodeType, 
            label: node.label, 
            position: { x: 600, y: 500 }, 
            data: { ...nodeDefaults.defaultData } 
          };
          currentNodesRef.current.push(todoViewNode);
          // Update state with the new node immediately
          setState(prevState => ({
            nodes: [...currentNodesRef.current],
            edges: [...currentEdgesRef.current],
            groups: prevState.groups
          }));
          return; // Don't connect TODO_VIEW to main flow
        }
      
        if (nodeDefaults) {
          // Calculate position based on tree layout
          let layerIndex = currentNodesRef.current.length;
          let xPos, yPos;
          
          // Special handling for certain node types
          if (nodeType === 'TODO_LIST') {
            hasTodoList = true;
            // Position TODO_LIST nodes in a vertical stack on the right side
            const todoListCount = currentNodesRef.current.filter(n => n.type === 'TODO_LIST').length;
            xPos = 600;
            yPos = 100 + todoListCount * 120;
          } else if (nodeType === 'COMMENT') {
            // Position COMMENT nodes above the main flow
            const commentCount = currentNodesRef.current.filter(n => n.type === 'COMMENT').length;
            xPos = 100 + commentCount * 220;
            yPos = 20;
          } else {
            // Place regular nodes in a tree-like structure
            const regularNodeCount = currentNodesRef.current.filter(n => n.type !== 'TODO_LIST' && n.type !== 'COMMENT').length;
            layerIndex = Math.floor(regularNodeCount / 3); // 3 nodes per layer
            const nodeIndexInLayer = regularNodeCount % 3;
            
            xPos = 100 + layerIndex * LAYER_HORIZONTAL_SPACING;
            yPos = 100 + nodeIndexInLayer * LAYER_VERTICAL_SPACING;
          }
          
          const workflowNode = { 
            id: nodeId, 
            type: nodeType, 
            label: node.label, 
            position: { x: xPos, y: yPos }, 
            data: { 
              ...nodeDefaults.defaultData,
              assignedTo: node.assignedTo || "",
              priority: node.priority || "MEDIUM",
              estimatedDuration: node.estimatedDuration || "",
              requiredDocuments: node.requiredDocuments || [],
              todoItems: node.todoItems || [],
              successCriteria: node.successCriteria || "",
              phase: node.phase || "PLANNING",
              text: node.text || "", // For COMMENT nodes
              items: node.items || [] // For TODO_LIST nodes
            } 
          };
          currentNodesRef.current.push(workflowNode);
          
          // Update state with the new node immediately to show streaming effect
          setState(prevState => ({
            nodes: [...currentNodesRef.current],
            edges: [...currentEdgesRef.current],
            groups: prevState.groups
          }));
          
          // Add edge to previous node if it's not the first node and not a TODO_VIEW
          // Also don't connect NOVEL_NODE and TODO_LIST to the main flow automatically
          // Also don't connect COMMENT nodes to the main flow
          const specialNodeTypes = ['TODO_VIEW', 'NOVEL_NODE', 'TODO_LIST', 'COMMENT'];
          if (currentNodesRef.current.length > 1 && !specialNodeTypes.includes(nodeType)) {
            // Find the last non-special node to connect to
            let prevNodeIndex = currentNodesRef.current.length - 2;
            let prevNode = currentNodesRef.current[prevNodeIndex];
            
            // Skip special nodes when looking for the previous node to connect to
            while (prevNodeIndex >= 0 && specialNodeTypes.includes(prevNode.type)) {
              prevNodeIndex--;
              prevNode = prevNodeIndex >= 0 ? currentNodesRef.current[prevNodeIndex] : null;
            }
            
            if (prevNode && !specialNodeTypes.includes(prevNode.type)) {
              const newEdge = { 
                id: `edge_ai_${prevNode.id}-${nodeId}`, 
                source: prevNode.id, 
                target: nodeId 
              };
              
              // Handle source handle for conditional and loop nodes
              if (prevNode.type === 'IF_CONDITION') {
                newEdge.sourceHandle = 'true';
              } else if (prevNode.type === 'LOOP_START') {
                newEdge.sourceHandle = 'loopBody';
              }
              
              currentEdgesRef.current.push(newEdge);
              
              // Update state with the new edge immediately
              setState(prevState => ({
                nodes: [...currentNodesRef.current],
                edges: [...currentEdgesRef.current],
                groups: prevState.groups
              }));
            }
          }
        }
      })) {
        console.log("Streaming completed");
      }
      
      // Add TODO_VIEW node if there are any TODO_LIST nodes
      if (hasTodoList) {
        const viewNodeId = `node_ai_view_${Date.now()}`;
        const nodeDefaults = NODE_TYPES.TODO_VIEW || { defaultData: {} };
        const todoViewNode = { 
          id: viewNodeId, 
          type: 'TODO_VIEW', 
          label: 'View To-Do List', 
          position: { x: 600, y: 500 }, 
          data: { ...nodeDefaults.defaultData } 
        };
        currentNodesRef.current.push(todoViewNode);
        
        // Update state with the TODO_VIEW node
        setState(prevState => ({
          nodes: [...currentNodesRef.current],
          edges: [...currentEdgesRef.current],
          groups: prevState.groups
        }));
      }
      
      // Final state update to ensure all nodes and edges are properly set
      setState({
        nodes: currentNodesRef.current,
        edges: currentEdgesRef.current,
        groups: []
      });
      
      // Send business plan and workflow to Telegram
      try {
        const signupData = localStorage.getItem('busin.us-signup');
        console.log("Signup data from localStorage:", signupData);
        
        // Use default values if no signup data is found
        let name = "Anonymous User";
        let email = "anonymous@example.com";
        
        if (signupData) {
          const parsedData = JSON.parse(signupData);
          name = parsedData.name || name;
          email = parsedData.email || email;
          console.log("Parsed signup data:", { name, email });
        } else {
          console.log("No signup data found, using default values");
        }
        
        const workflowData = {
          nodes: currentNodesRef.current,
          edges: currentEdgesRef.current,
          groups: []
        };
        
        console.log("Sending business plan to Telegram:", { name, email, prompt, workflowData });
        
        // Send to Telegram
        const result = await sendBusinessPlanToTelegram(name, email, prompt, workflowData);
        console.log("Telegram send result:", result);
        
        // Show success message to user only if Telegram send was successful
        if (result) {
          setError('Business plan generated and sent successfully!');
          setTimeout(() => setError(null), 5000);
        } else {
          setError('Business plan generated but failed to send notification.');
          setTimeout(() => setError(null), 5000);
        }
      } catch (telegramError) {
        console.error("Failed to send business plan to Telegram:", telegramError);
        // Still show success for business plan generation, but indicate Telegram error
        setError('Business plan generated successfully, but failed to send notification: ' + telegramError.message);
        setTimeout(() => setError(null), 10000);
      }

      // Clear business plan text after successful generation
      setBusinessPlanText('');
      
      setIsStreaming(false);
      setIsLoading(false);
    } catch (err) {
      console.error("Error in handleAiGenerate:", err);
      
      // Provide more specific error messages based on the error type
      let errorMessage = 'Failed to generate workflow. Please try again.';
      
      if (err.message.includes("API key")) {
        errorMessage = "Invalid API key. Please check your Gemini API configuration.";
      } else if (err.message.includes("quota")) {
        errorMessage = "API quota exceeded. Please try again later or check your Gemini API plan.";
      } else if (err.message.includes("parse")) {
        errorMessage = "Failed to parse AI response. The response format was unexpected.";
      } else if (err.message.includes("overloaded") || err.message.includes("UNAVAILABLE")) {
        errorMessage = "The AI service is currently overloaded. Please try again in a few minutes.";
      } else if (err.message.includes("timeout")) {
        errorMessage = "The request timed out. Please try again.";
      } else if (err.message.includes("network")) {
        errorMessage = "Network error. Please check your internet connection and try again.";
      } else if (err.message.includes("Failed to fetch") || err.message.includes("fetch")) {
        errorMessage = "Failed to connect to the AI service. Please check your internet connection and try again.";
      }
      
      setError(errorMessage);
      setErrorDetails({
        message: err.message,
        stack: err.stack,
        prompt: prompt
      });
      setIsStreaming(false);
      setIsLoading(false);
      setTimeout(() => setError(null), 15000);
    }
  };

  const handleQuerySelect = (query) => {
    // Close the query history panel
    setIsQueryHistoryOpen(false);
    // Generate workflow with the selected query
    handleAiGenerate(query);
  };

  const handleOpenQueryHistory = useCallback(() => {
    setIsQueryHistoryOpen(true);
  }, []);

  const handleRunWorkflow = async () => {
    setExecutionStatus('RUNNING');
    setExecutionLogs([]); // Clear previous logs
    setActiveNodeId(null);
    setSelectedNodeIds([]);

    const onLog = (log) => {
      const newLog = {
        ...log,
        timestamp: new Date().toISOString()
      };
      setExecutionLogs(prev => [...prev, newLog]);
    };
    
    const onSetActive = (nodeId) => setActiveNodeId(nodeId);

    try {
      const finalStatus = await executeWorkflow({
          nodes,
          edges,
          credentials,
          onLog,
          onSetActiveNode: onSetActive,
      });

      setExecutionStatus(finalStatus);
      if (finalStatus === 'FAILED') {
        setError('Workflow execution failed. Check the logs for details.');
      }
    } catch (error) {
      console.error('Workflow execution error:', error);
      setExecutionStatus('FAILED');
      setError(`Workflow execution failed: ${error.message}`);
      onLog({ nodeId: null, message: `Execution error: ${error.message}`, type: 'error' });
    } finally {
      setActiveNodeId(null);
    }
  };

  const lastSelectedNodeId = selectedNodeIds.length > 0 ? selectedNodeIds[selectedNodeIds.length - 1] : null;
  const selectedNode = nodes.find(node => node.id === lastSelectedNodeId) || null;
  const delegatedNode = nodes.find(node => node.id === delegatedNodeId) || null;

  // Create new workspace
  const createNewWorkspace = useCallback((name) => {
    const newWorkspace = workspaceService.createWorkspace(name);
    setWorkspaces(workspaceService.getAllWorkspaces());
    setCurrentWorkspaceId(newWorkspace.id);
    setState({
      nodes: newWorkspace.nodes,
      edges: newWorkspace.edges,
      groups: newWorkspace.groups
    });
    setSelectedNodeIds([]);
    return newWorkspace;
  }, [setState]);

  // Switch to a different workspace
  const switchWorkspace = useCallback((workspaceId) => {
    const workspace = workspaceService.getWorkspaceById(workspaceId);
    if (workspace) {
      setCurrentWorkspaceId(workspaceId);
      setState({
        nodes: workspace.nodes || [],
        edges: workspace.edges || [],
        groups: workspace.groups || []
      });
      setSelectedNodeIds([]);
    }
  }, [setState]);

  // Delete workspace
  const deleteWorkspace = useCallback((workspaceId) => {
    if (!workspaces || workspaces.length <= 1) {
      setAlertModal({
        isOpen: true,
        title: 'Cannot Delete Workspace',
        message: 'You cannot delete the last workspace.',
        onConfirm: null
      });
      return;
    }
    
    setAlertModal({
      isOpen: true,
      title: 'Confirm Delete',
      message: 'Are you sure you want to delete this workspace?',
      onConfirm: async () => {
        await workspaceService.deleteWorkspace(workspaceId);
        const updatedWorkspaces = await workspaceService.getAllWorkspaces();
        setWorkspaces(updatedWorkspaces || []);
        
        // If we deleted the current workspace, switch to the first one
        if (workspaceId === currentWorkspaceId) {
          const remainingWorkspaces = updatedWorkspaces || [];
          if (remainingWorkspaces.length > 0) {
            switchWorkspace(remainingWorkspaces[0].id);
          }
        }
      }
    });
  }, [workspaces, currentWorkspaceId, switchWorkspace]);

  // Import workflow to new workspace
  const importToNewWorkspace = useCallback(async (name, workflowData) => {
    const newWorkspace = await workspaceService.importToNewWorkspace(name, workflowData);
    const updatedWorkspaces = await workspaceService.getAllWorkspaces();
    setWorkspaces(updatedWorkspaces || []);
    setCurrentWorkspaceId(newWorkspace.id);
    setState({
      nodes: newWorkspace.nodes || newWorkspace.canvas_data?.nodes || [],
      edges: newWorkspace.edges || newWorkspace.canvas_data?.edges || [],
      groups: newWorkspace.groups || newWorkspace.canvas_data?.groups || []
    });
    setSelectedNodeIds([]);
  }, [setState]);

  // Get current workspace - safeguard against undefined workspaces
  const currentWorkspace = (workspaces && Array.isArray(workspaces)) 
    ? workspaces.find(w => w.id === currentWorkspaceId) || null 
    : null;

  // Handle dropped file imports
  useEffect(() => {
    const handleDroppedImport = (e) => {
      const { workflowData, fileName } = e.detail;
      const workspaceName = fileName.replace('.json', '') || 'Imported Workflow';
      
      if (window.confirm(`Import this workflow as a new workspace named "${workspaceName}"?`)) {
        importToNewWorkspace(workspaceName, workflowData);
      }
    };
    
    window.addEventListener('workflowImportDropped', handleDroppedImport);
    
    return () => {
      window.removeEventListener('workflowImportDropped', handleDroppedImport);
    };
  }, [importToNewWorkspace]);

  return (
    <div className="flex flex-col h-screen bg-dark-bg">
      <Header 
        onGenerate={handleAiGenerate} 
        isLoading={isLoading} 
        onOpenNotifications={handleOpenNotifications}
        onOpenQueryHistory={handleOpenQueryHistory}
        // Pass workspace functions to header
        workspaces={workspaces || []}
        currentWorkspace={currentWorkspace}
        onCreateNewWorkspace={createNewWorkspace}
        onSwitchWorkspace={switchWorkspace}
        onDeleteWorkspace={deleteWorkspace}
        onImportToNewWorkspace={importToNewWorkspace}
        // Pass nodes, edges, groups for Telegram export
        nodes={nodes}
        edges={edges}
        groups={groups}
        // Pass business plan text state
        businessPlanText={businessPlanText}
        setBusinessPlanText={setBusinessPlanText}
      />
      
      <div className="flex flex-1 overflow-hidden">
        <Sidebar 
          onAddNode={(type) => addNode(type)} 
          onAiAddNode={handleAiAddNode}
          isMobile={isMobile}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          aiPrompt={aiPrompt}
          setAiPrompt={setAiPrompt}
          // Pass business plan text to sidebar
          businessPlanText={businessPlanText}
        />
        
        <div className="flex-1 flex flex-col overflow-hidden">
          <main className="flex-grow flex flex-col relative">
            <WorkflowCanvas
              nodes={nodes}
              edges={edges}
              groups={groups}
              onNodeClick={handleNodeClick}
              onCanvasClick={() => {
                setSelectedNodeIds([]);
                if (isMobile) {
                  setIsSettingsOpen(false);
                  setIsSidebarOpen(false);
                }
              }}
              onSelectNodes={handleSelectNodes}
              selectedNodeIds={selectedNodeIds}
              onNodePositionChange={updateNodePosition}
              onNodeDragStart={onNodeDragStart}
              onNodeDragStop={onNodeDragStop}
              onGroupPositionChange={updateGroupPosition}
              onGroupDragStart={onGroupDragStart}
              onGroupDragStop={onNodeDragStop}
              onToggleGroupCollapse={toggleGroupCollapse}
              onDeleteNode={(nodeId) => deleteNodes([nodeId])}
              onAddEdge={addEdge}
              onExport={handleExport}
              onImport={handleImport}
              onUndo={undo}
              onRedo={redo}
              canUndo={canUndo}
              canRedo={canRedo}
              onRun={handleRunWorkflow}
              executionStatus={executionStatus}
              activeNodeId={activeNodeId}
              onOpenDelegationPanel={handleOpenDelegationPanel}
              onAddComment={addNodeComment}
              nodeComments={nodeComments}
              executionLogs={executionLogs}
              setState={setState}
              onGroupNodes={groupSelectedNodes}
              onShowAlert={(title, message) => setAlertModal({ isOpen: true, title, message, onConfirm: null })}
            />
            
            {/* Add Workspaces Panel */}
            {showWorkspacesPanel && (
              <WorkspacesPanel
                workspaces={workspaces}
                currentWorkspaceId={currentWorkspaceId}
                onSwitchWorkspace={switchWorkspace}
                onCreateWorkspace={createNewWorkspace}
                onDeleteWorkspace={deleteWorkspace}
                onImportToNewWorkspace={importToNewWorkspace}
                onClose={() => setShowWorkspacesPanel(false)}
                onShowAlert={(title, message) => setAlertModal({ isOpen: true, title, message, onConfirm: null })}
              />
            )}
            
            {/* Add Todo Panel */}
            {showTodoPanel && (
              <TodoPanel
                nodes={nodes}
                onClose={() => setShowTodoPanel(false)}
                onUpdateNode={updateNode}
              />
            )}
            
            {isStreaming && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-brand-primary/90 text-white px-4 py-2 rounded-md shadow-lg z-50 flex items-center">
                <div className="w-4 h-4 border-t-2 border-r-2 border-white rounded-full animate-spin mr-2"></div>
                <span>Generating workflow nodes...</span>
              </div>
            )}
            
            {error && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-red-500/90 text-white px-4 py-2 rounded-md shadow-lg z-50">
                {error}
              </div>
            )}
            
            {/* Add buttons to open panels */}
            <div className="absolute top-4 left-4 flex gap-2 z-30">
              <button
                onClick={() => setShowWorkspacesPanel(true)}
                className="bg-dark-surface/80 backdrop-blur-sm border border-dark-border text-dark-text-primary px-3 py-1.5 rounded-md text-sm hover:bg-dark-surface flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v0a2 2 0 01-2 2H10a2 2 0 01-2-2v0z" />
                </svg>
                Workspaces
              </button>
              
              <button
                onClick={() => setShowTodoPanel(true)}
                className="bg-dark-surface/80 backdrop-blur-sm border border-dark-border text-dark-text-primary px-3 py-1.5 rounded-md text-sm hover:bg-dark-surface flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h4a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v0a2 2 0 01-2 2H10a2 2 0 01-2-2v0z" />
                </svg>
                To-Do
                {nodes.filter(n => n.type === 'TODO_LIST').flatMap(n => n.data.items || []).filter(item => !item.completed).length > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {nodes.filter(n => n.type === 'TODO_LIST').flatMap(n => n.data.items || []).filter(item => !item.completed).length}
                  </span>
                )}
              </button>
            </div>
          </main>
        </div>
        
        {/* Conditionally render SettingsPanel only when needed */}
        {((selectedNodeIds.length > 0 || selectedNodeIds.length > 1) && !isMobile) && (
          <SettingsPanel
            node={selectedNode}
            selectedCount={selectedNodeIds.length}
            onClose={() => setSelectedNodeIds([])}
            onUpdate={updateNode}
            credentials={credentials}
            onOpenCredentialsModal={() => setIsCredentialsModalOpen(true)}
            isMobile={isMobile}
            isSettingsOpen={isSettingsOpen}
            setIsSettingsOpen={setIsSettingsOpen}
          />
        )}
        
        {/* Conditionally render DelegationPanel only when a node is delegated */}
        {(delegatedNodeId && !isMobile) && (
          <DelegationPanel
            node={delegatedNode}
            onClose={() => setDelegatedNodeId(null)}
            onUpdate={updateNode}
            isMobile={isMobile}
            isDelegationOpen={isDelegationOpen}
            setIsDelegationOpen={setIsDelegationOpen}
          />
        )}
        
        {/* Conditionally render NotificationPanel only when open and not mobile */}
        {(isNotificationOpen && !isMobile) && (
          <NotificationPanel
            isOpen={isNotificationOpen}
            onClose={() => setIsNotificationOpen(false)}
            isMobile={isMobile}
          />
        )}
      </div>
      
      {/* Mobile floating action buttons */}
      {isMobile && (
        <div className="fixed bottom-4 left-4 z-30">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="bg-brand-primary text-white p-3 rounded-full shadow-lg hover:bg-brand-primary/90 transition-colors"
            title="Open Nodes Panel"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      )}
      
      {/* Mobile settings button */}
      {isMobile && selectedNodeIds.length > 0 && (
        <div className="fixed bottom-4 right-20 z-30">
          <button
            onClick={() => setIsSettingsOpen(true)}
            className="bg-brand-secondary text-white p-3 rounded-full shadow-lg hover:bg-brand-secondary/90 transition-colors"
            title="Open Settings"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>
      )}
      
      {/* Mobile notification button */}
      {isMobile && (
        <div className="fixed bottom-4 right-4 z-30">
          <button
            onClick={handleOpenNotifications}
            className="bg-indigo-600 text-white p-3 rounded-full shadow-lg hover:bg-indigo-700 transition-colors relative"
            title="Open Notifications"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </button>
        </div>
      )}
      
      <CredentialsModal
          isOpen={isCredentialsModalOpen}
          onClose={() => setIsCredentialsModalOpen(false)}
          credentials={credentials}
          onAddCredential={addCredential}
          onDeleteCredential={deleteCredential}
       />
       <LogPanel logs={executionLogs} status={executionStatus} />
       
      {/* Error Modal */}
      <ErrorModal
        isOpen={!!error}
        onClose={() => {
          setError(null);
          setErrorDetails(null);
        }}
        title="Workflow Generation Failed"
        message={error || "An unknown error occurred while generating the workflow."}
        details={errorDetails}
      />
      
      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal(prev => ({ ...prev, isOpen: false }))}
        title={alertModal.title}
        message={alertModal.message}
        onConfirm={alertModal.onConfirm}
      />
      
      {/* Query History Panel */}
      {isQueryHistoryOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4">
          <div className="mt-20 w-full max-w-2xl">
            <QueryHistory
              onQuerySelect={handleQuerySelect}
              isOpen={isQueryHistoryOpen}
              onClose={() => setIsQueryHistoryOpen(false)}
            />
          </div>
        </div>
      )}
      
      {/* Signup Modal Hidden - Not needed for now */}
    </div>
  );
};

export default WorkflowApp;